import * as React from 'react';
import WWEvents from '../WWEvents';

interface UseCartTimerProps {
    startTimer: (seconds: number) => void;
    resetTimer: () => void;
    remainingTime: number;
}

const useCartTimer = (remindAt: number, showMinicartAt: number): UseCartTimerProps => {
    const [remainingTime, setRemainingTime] = React.useState(0);
    const [isTimerRunning, setIsTimerRunning] = React.useState(false);
    const [expirationTime, setExpirationTime] = React.useState(0);

    React.useEffect(() => {
        if (!isTimerRunning) {
            return;
        }

        if (remainingTime === 0) {
            WWEvents.fire('CART_EXPIRED', undefined);
            WWEvents.fire('CART_TIMER_TICK', 0);
            setIsTimerRunning(false);
            return;
        }

        WWEvents.fire('CART_TIMER_TICK', remainingTime);

        if (remainingTime === remindAt) {
            WWEvents.fire('CART_REMIND', undefined);
        }

        if (remainingTime <= remindAt) {
            WWEvents.fire('CART_EXPIRING', undefined);
        }

        if (remainingTime <= showMinicartAt) {
            WWEvents.fire('CART_NOTIFY', undefined);
        }
    }, [remindAt, showMinicartAt, isTimerRunning, remainingTime]);

    const startTimer = React.useCallback((seconds: number) => {
        setRemainingTime(seconds);
        setExpirationTime(Date.now() + seconds * 1000);
        setIsTimerRunning(true);
    }, []);

    const resetTimer = React.useCallback(() => {
        setRemainingTime(0);
        setExpirationTime(0);
    }, []);

    React.useEffect(() => {
        if (!isTimerRunning) {
            return undefined;
        }

        const cachedInterval = setInterval(() => {
            setRemainingTime(Math.max(0, Math.round((expirationTime - Date.now()) / 1000)));
        }, 1000);

        return () => clearInterval(cachedInterval);
    }, [expirationTime, isTimerRunning]);

    return {
        startTimer,
        resetTimer,
        remainingTime,
    };
};

export default useCartTimer;
